import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import { Button } from '@verclam/ui-kit';
import { TriangleAlert } from 'lucide-react';
import type * as React from 'react';

const containerStyle = css`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	text-align: center;
	padding: 3rem ${theme.layout.gutter};
	border-radius: ${theme.radii.xlarge};
	border: 1.5px solid color-mix(in srgb, ${theme.states.danger.ink} 24%, transparent);
	background-color: ${theme.states.danger.surface};
	color: ${theme.states.danger.ink};
`;

const iconStyle = css`
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${theme.states.danger.ink};
`;

const titleStyle = css`
	${theme.typography.stateTitle}
	margin: 0;
	color: ${theme.states.danger.ink};
`;

const descriptionStyle = css`
	margin: 0;
	max-width: 32rem;
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.medium};
	color: ${theme.states.danger.ink};
`;

const actionStyle = css`
	margin-top: 0.5rem;
`;

type Props = {
	readonly title: string;
	readonly description?: string;
	/** Absent when nothing can be retried (dead token): the button disappears in that case. */
	readonly onRetry?: () => void;
	readonly retryLabel?: string; // defaults to 'Réessayer'
};

/**
 * Explicit "could not load" state, used in place of a dead-end error message: a list, a card, any
 * block whose data failed to fetch. Carries the `danger` state pair, unlike `EmptyState`'s neutral
 * surface, and offers a way out through `onRetry` — omitting it (a dead token, nothing left to
 * retry) hides the button entirely rather than rendering one that can do nothing.
 */
const ErrorState: React.FC<Props> = ({ title, description, onRetry, retryLabel = 'Réessayer' }) => {
	return (
		<div css={containerStyle}>
			<div css={iconStyle}>
				<TriangleAlert size={28} aria-hidden="true" />
			</div>
			<p css={titleStyle}>{title}</p>
			{description !== undefined ? <p css={descriptionStyle}>{description}</p> : null}
			{onRetry !== undefined ? (
				<div css={actionStyle}>
					<Button type="button" text label={retryLabel} onClick={onRetry} />
				</div>
			) : null}
		</div>
	);
};

export default ErrorState;
